import React, { useEffect, useMemo, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { Chip } from '@mui/material';

import { useApp } from 'contexts/AppContext';
import { useNotif } from 'contexts/NotifContext';
import { useTheme } from 'contexts/ThemeContext';
import { useUser } from 'contexts/UserContext';

import moment from 'utils/moment';
import pluralize from 'utils/pluralize';
import { ACTIVATION, GROUP, INSIGHT, INSIGHT_SHOPS } from 'utils/rest';

import { Text, Tooltip } from 'components/DataDisplay';
import { Modal } from 'components/Feedback';
import {
  Button,
  MultipleSelectSearch,
  Select,
  TextField
} from 'components/Inputs';
import { Container, Item, Row } from 'components/Layout';
import { Loading } from 'components/Loading';
import ModalDelete from 'components/ModalDelete';
import { TableData } from 'components/TableData';
import { UnauthorizedModal } from 'components/UnauthorizedModal';

import { ModalForm } from './components/ModalForm';

const STATUS_OPTIONS = [
  { label: 'Actifs', value: 'false' },
  { label: 'Archivés', value: 'true' },
  { label: 'Tous', value: 'all' }
];

const NOT_ARCHIVABLE_TOOLTIP =
  'Archivage impossible : ce magasin a des campagnes en cours';

const ShopContainer = () => {
  let timer;
  const [medias, setMedias] = useState([]);
  const [rows, setRows] = useState([]);
  const [searchFieldValue, setSearchFieldValue] = useState('');
  const [pageNumber, setPageNumber] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [openModal, setModal] = useState(null);
  const [selected, setSelected] = useState({});
  const [shopGroups, setShopGroups] = useState([]);
  const [selectedShopGroups, setSelectedShopGroups] = useState([]);
  const [statusFilter, setStatusFilter] = useState('false');
  const [selectedShopIds, setSelectedShopIds] = useState([]);
  const { themeColors } = useTheme();
  const { adminRights, user, profile } = useUser();
  const { domainConfig, isSimplify } = useApp();
  const { notify } = useNotif();

  const closeModal = () => {
    setModal(null);
    setSelected({});
  };

  const selectShop = async id => {
    const shop = await ACTIVATION.getActivationShop(id);
    setSelected(shop);

    return shop;
  };

  const getToolTipText = txt => {
    return (
      <Row spacing={0}>
        <Item>
          <Text color={themeColors.dark}>{txt}</Text>
        </Item>
      </Row>
    );
  };

  const getDatas = async () => {
    const [shopGroupsList, mediasList] = await Promise.all([
      ACTIVATION.getActivationShopGoups(),
      ACTIVATION.getActivableMedias()
    ]);

    setShopGroups(shopGroupsList);
    setMedias(mediasList);

    setLoaded(true);
  };

  const getShops = async () => {
    try {
      const payload = {
        limit,
        page: pageNumber + 1,
        search: searchFieldValue,
        withActivationShopGroups: true,
        archived: statusFilter
      };

      if (selectedShopGroups.length > 0) {
        payload.activationShopGroups = selectedShopGroups.map(
          ({ value }) => value
        );
      }

      const {
        results,
        totalCount
      } = await ACTIVATION.getActivationShopsPaginated(payload);
      setTotal(totalCount);
      return results;
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const loadShops = async () => {
    const newShops = await getShops();

    const mapped = newShops.map(item => {
      const newItem = { ...item };
      return newItem;
    }, []);
    setRows(mapped);
  };

  const updateGroup = async (data, activationShopId) => {
    const group = await GROUP.getGroupByShop({
      domain: user.domain,
      name: data.name,
      activationShopId
    });

    if (group) {
      await GROUP.updateV2(group._id, {
        userIds: data.users,
        activationShopId
      });
    }
  };
  const updateShopGroups = async (shopId, shopGroupList) => {
    const oldShopGroups = shopGroups.filter(el => el.shops.includes(shopId));
    const toAdd = shopGroupList.filter(
      el => !oldShopGroups.some(old => old._id === el.value)
    );
    const toRemove = oldShopGroups.filter(
      old => !shopGroupList.some(el => old._id === el.value)
    );

    if (toAdd.length > 0) {
      await Promise.all(
        toAdd.map(shopGroup =>
          ACTIVATION.addShopIdsToShopGoup(shopGroup.value, shopId, true)
        )
      );
    }

    if (toRemove.length > 0) {
      await Promise.all(
        toRemove.map(shopGroup =>
          ACTIVATION.removeShopIdsToShopGoup(shopGroup._id, shopId)
        )
      );
    }
  };

  const convertTime = ({ time, unit }) => {
    let val = 0;
    if (unit === 'min') {
      val = parseFloat(time);
    } else {
      val = parseFloat(time) * 60;
    }
    // if the time is equal to 120 minute then 119 minutes is registered in db
    return val === 120 ? 119 : val;
  };

  const generateParametersAndPolygons = async ({
    audienceType,
    parameters
  }) => {
    switch (audienceType) {
      case 'isochrony': {
        return {
          parameters: {
            transportation: parameters.transportation,
            unit: 'min',
            // doit absolument etre en minute pour que le compute fonctionne
            time: convertTime(parameters)
          },
          audienceType,
          polygons: []
        };
      }
      case 'radius': {
        return { parameters, audienceType, polygons: [] };
      }
      default: {
        const rslt = await INSIGHT.getGeoZone({
          searchFields: parameters.map(el => ({
            type: el.type,
            searchField: el.key
          })),
          geo: true
        });

        const polygons = rslt.map(geoZone => ({
          ...geoZone.geoShape
        }));
        return {
          audienceType,
          polygons,
          parameters
        };
      }
    }
  };

  const updateInsigtShop = async ({
    original: insigtShop,
    lat,
    lng,
    address,
    zdc
  }) => {
    if (!insigtShop?.origin?.coordinates) {
      return;
    }
    if (
      lat !== insigtShop.origin.coordinates[1] ||
      lng !== insigtShop.origin.coordinates[0] ||
      zdc.audienceType !== insigtShop.audienceType ||
      zdc.parameters !== insigtShop.parameters
    ) {
      const origin = { coordinates: [lng, lat], type: 'Point' };
      const params = await generateParametersAndPolygons(zdc);
      await INSIGHT_SHOPS.update(insigtShop._id, {
        origin,
        address,
        ...params
      });
      if (zdc.audienceType === 'isochrony') {
        await INSIGHT_SHOPS.computeInsightShop(insigtShop._id);
      }
    }
  };

  const openUpdate = async id => {
    if (adminRights?.pml?.shop?.update || isSimplify) {
      await selectShop(id);
      setModal('modalUpdate');
    } else {
      setModal('unauthorized');
    }
  };

  const onUpdate = async ({ insigtShop, ...data }, newShopGroups) => {
    const rslt = await ACTIVATION.updateActivationShop(selected._id, {
      ...selected,
      ...data
    });
    if (rslt._id) {
      await updateInsigtShop(insigtShop);
      await updateShopGroups(rslt._id, newShopGroups);
      await updateGroup(data, selected._id);
    }
    closeModal();
    setLoaded(false);
  };

  const openDelete = async id => {
    if (adminRights?.pml?.shop?.delete || isSimplify) {
      await selectShop(id);
      setModal('modalDelete');
    } else {
      setModal('unauthorized');
    }
  };

  const onDelete = async () => {
    const deleted = await ACTIVATION.deleteActivationShop(selected.id);
    closeModal();
    setLoaded(false);
    if (!deleted) {
      notify({
        message:
          'Ce magasin n`est pas supprimable car il est lié à une campagne validée, active, en pause, terminée ou annulée.',
        variant: 'error',
        time: 8000
      });
    }
  };

  const reloadAfterArchiving = () => {
    setSelectedShopIds([]);
    setLoaded(false);
  };

  const onArchive = async () => {
    const result = await ACTIVATION.archiveActivationShops(selectedShopIds);
    closeModal();

    if (!result) {
      notify({
        message: "L'archivage a échoué.",
        variant: 'error',
        time: 8000
      });
      reloadAfterArchiving();
      return;
    }

    const skippedNames = result.skipped.map(shop => shop.name).join(', ');

    if (result.archived.length > 0) {
      notify({
        message: `${result.archived.length} point(s) de vente archivé(s)`,
        variant: 'success',
        time: 5000
      });
    }

    if (skippedNames) {
      notify({
        message: `Non archivé(s), une campagne a changé de statut entre-temps : ${skippedNames}`,
        variant: 'error',
        time: 8000
      });
    }

    reloadAfterArchiving();
  };

  const onUnarchive = async () => {
    const result = await ACTIVATION.unarchiveActivationShops(selectedShopIds);
    closeModal();

    if (!result) {
      notify({
        message: 'Le désarchivage a échoué.',
        variant: 'error',
        time: 8000
      });
      reloadAfterArchiving();
      return;
    }

    notify({
      message: `${result.unarchived.length} point(s) de vente désarchivé(s)`,
      variant: 'success',
      time: 5000
    });
    reloadAfterArchiving();
  };

  const formatStatus = (archived, row) => {
    const chipStyle = {
      backgroundColor: archived
        ? themeColors.badgeArchived
        : themeColors.badgeActif,
      borderRadius: '12px',
      fontWeight: 500,
      fontSize: '12px'
    };

    if (!archived) {
      return <Chip label="Actif" style={chipStyle} />;
    }

    const chip = <Chip label="Archivé" style={chipStyle} />;

    if (!row.archivedAt) {
      return chip;
    }

    const archivedDate = moment(row.archivedAt).format('DD/MM/YYYY');
    const archivedLabel = row.archivedBy
      ? `Archivé le ${archivedDate}`
      : `Archivé automatiquement le ${archivedDate}`;

    return <Tooltip title={archivedLabel}>{chip}</Tooltip>;
  };

  const formatShopGroups = (list = [], type, color) => {
    const count = list.length;
    if (count === 0) {
      return <Text>Aucun</Text>;
    }
    if (count === 1) {
      return <Text color={color}> {list[0]} </Text>;
    }

    const others = count - 1;
    const str = ` ${others} ${pluralize('autre', others)} ${pluralize(
      type,
      others
    )}`;

    const tooltipContent = () => {
      return <Container>{list.map(item => getToolTipText(item))}</Container>;
    };
    return (
      <Container>
        <Row spacing={2}>
          <Item flex>
            <Text color={color}> {list[0]}&nbsp;</Text>
          </Item>
          <Item flex>
            <Text> et&nbsp;</Text>
          </Item>
          <Item flex>
            <Tooltip title={tooltipContent()}>
              <Text color={color} link>
                {str}
              </Text>
            </Tooltip>
          </Item>
        </Row>
      </Container>
    );
  };

  useEffect(() => {
    if (!loaded) {
      getDatas();
    }
    // eslint-disable-next-line
  }, [loaded]);

  useEffect(() => {
    if (loaded) {
      clearTimeout(timer);
      // eslint-disable-next-line
      timer = setTimeout(loadShops, 500);
    }
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [
    loaded,
    pageNumber,
    limit,
    searchFieldValue,
    selectedShopGroups,
    statusFilter
  ]);

  const shopGroupsWithShopsOptions = useMemo(() => {
    const options = shopGroups.reduce((accumulator, shopGroup) => {
      if ((shopGroup.shops || []).length > 0) {
        accumulator.push({
          label: shopGroup.name,
          value: shopGroup._id
        });
      }
      return accumulator;
    }, []);

    return options.sort((leftOption, rightOption) =>
      leftOption.label.localeCompare(rightOption.label)
    );
  }, [shopGroups]);

  const { shopGroupName, shopName } = domainConfig.groupTaxonomy?.reduce(
    (acc, item) => {
      if (item.level === 'shop') {
        acc.shopName = item.name;
      } else if (Number(acc?.shopGroupRef?.level || 0) <= Number(item.level)) {
        acc.shopGroupRef = item;
        acc.shopGroupName = item.name;
      }
      return acc;
    },
    {
      shopGroupRef: null,
      shopGroupName: 'Groupe de magasins',
      shopName: 'Magasins'
    }
  );

  const canArchive = !!profile?.features?.archiveShops;

  const selectedRows = rows.reduce((acc, row) => {
    if (selectedShopIds.includes(row.id)) {
      acc.push(row);
    }
    return acc;
  }, []);

  const selectedArchivedCount = selectedRows.reduce(
    (acc, row) => (row.archived ? acc + 1 : acc),
    0
  );

  const isArchiveSelection =
    selectedRows.length > 0 && selectedArchivedCount === 0;
  const isUnarchiveSelection =
    selectedRows.length > 0 && selectedArchivedCount === selectedRows.length;

  const isRowArchivable = row => row.archivable !== false;

  return (
    <Container>
      <Row spacing={4} justify="flex-start">
        <Item xs={3}>
          <TextField
            onChange={e => {
              setPageNumber(0);
              setSearchFieldValue(e);
            }}
            value={searchFieldValue}
            title="Recherche un point de vente"
            placeholder="Key, Nom, ..."
            type="search"
            unit={
              <SearchIcon
                style={{
                  color: themeColors.light
                }}
              />
            }
          />
        </Item>
        <Item xs={3} justify="flex-start">
          <MultipleSelectSearch
            title={shopGroupName}
            options={shopGroupsWithShopsOptions}
            values={selectedShopGroups}
            onChange={options => {
              setPageNumber(0);
              setSelectedShopGroups(options);
            }}
            small
          />
        </Item>
        <Item xs={2} justify="flex-start">
          <Select
            title="Statut"
            value={statusFilter}
            options={STATUS_OPTIONS}
            onChange={value => {
              setPageNumber(0);
              setSelectedShopIds([]);
              setStatusFilter(value);
            }}
            small
          />
        </Item>
      </Row>
      {canArchive && selectedRows.length > 0 && (
        <Row spacing={4} justify="space-between">
          <Item flex>
            <Text>{selectedRows.length} point(s) de vente sélectionné(s)</Text>
          </Item>
          <Item flex>
            {isArchiveSelection && (
              <Button
                rounded
                size="medium"
                onClick={() => setModal('modalArchive')}
              >
                {`Archiver (${selectedRows.length})`}
              </Button>
            )}
            {isUnarchiveSelection && (
              <Button
                rounded
                size="medium"
                onClick={() => setModal('modalUnarchive')}
              >
                {`Désarchiver (${selectedRows.length})`}
              </Button>
            )}
          </Item>
        </Row>
      )}
      <Row spacing={4}>
        <Item>
          <Loading loading={!loaded} />
          {loaded && (
            <TableData
              key={statusFilter}
              rows={rows}
              showCheckboxes={canArchive}
              onCheck={setSelectedShopIds}
              isRowSelectable={isRowArchivable}
              selectOnRowClick={false}
              unselectableRowTooltip={NOT_ARCHIVABLE_TOOLTIP}
              headers={[
                { label: 'Key', id: 'key', sortKey: 'key' },
                { label: shopName, id: 'name', sortKey: 'name' },
                {
                  label: shopGroupName,
                  id: 'shopGroups',
                  format: (value = []) => {
                    return formatShopGroups(
                      value.map(el => el.name),
                      shopGroupName,
                      'pml'
                    );
                  }
                },
                { label: 'Statut', id: 'archived', format: formatStatus }
              ]}
              hidenFields={['_id']}
              onUpdate={openUpdate}
              onDelete={openDelete}
              pageNumber={pageNumber}
              numberPerRow={limit}
              overrideCount={total}
              skipSlice
              getPageNumber={page => {
                setPageNumber(page);
              }}
              setNumberPerRow={newLimit => {
                setLimit(newLimit);
                setPageNumber(0);
              }}
            />
          )}
          {openModal === 'modalUpdate' && (
            <ModalForm
              open={openModal === 'modalUpdate'}
              onClose={closeModal}
              onChange={onUpdate}
              shopGroupName={shopGroupName}
              config={{ medias, users: [], selected, shopGroups }}
            />
          )}
          {openModal === 'modalDelete' && (
            <ModalDelete
              name={selected.name}
              type="ce point de vente"
              open={openModal === 'modalDelete'}
              onClose={closeModal}
              onDelete={onDelete}
            />
          )}
          {openModal === 'modalArchive' && (
            <Modal
              open
              onClose={closeModal}
              titleHead={`Archiver ${selectedRows.length} point(s) de vente ?`}
              maxWidth="sm"
            >
              <Container>
                <Row>
                  <Item>
                    <Text>
                      Les points de vente archivés n’apparaîtront plus sur la
                      plateforme. Les funnels et connecteurs de leurs campagnes
                      seront désactivés. Vous pourrez les désarchiver à tout
                      moment.
                    </Text>
                  </Item>
                </Row>
                <Row>
                  <Item xs={12} sm={6}>
                    <Button
                      variant="outlined"
                      size="medium"
                      widthSize="medium"
                      rounded
                      onClick={closeModal}
                    >
                      ANNULER
                    </Button>
                  </Item>
                  <Item xs={12} sm={6}>
                    <Button
                      variant="outlined"
                      size="medium"
                      widthSize="medium"
                      rounded
                      onClick={onArchive}
                    >
                      ARCHIVER
                    </Button>
                  </Item>
                </Row>
              </Container>
            </Modal>
          )}
          {openModal === 'modalUnarchive' && (
            <Modal
              open
              onClose={closeModal}
              titleHead={`Désarchiver ${selectedRows.length} point(s) de vente ?`}
              maxWidth="sm"
            >
              <Container>
                <Row>
                  <Item>
                    <Text>
                      Ils réapparaîtront sur la plateforme et les funnels
                      désactivés lors de l’archivage seront réactivés.
                    </Text>
                  </Item>
                </Row>
                <Row>
                  <Item xs={12} sm={6}>
                    <Button
                      variant="outlined"
                      size="medium"
                      widthSize="medium"
                      rounded
                      onClick={closeModal}
                    >
                      ANNULER
                    </Button>
                  </Item>
                  <Item xs={12} sm={6}>
                    <Button
                      variant="outlined"
                      size="medium"
                      widthSize="medium"
                      rounded
                      onClick={onUnarchive}
                    >
                      DÉSARCHIVER
                    </Button>
                  </Item>
                </Row>
              </Container>
            </Modal>
          )}
          {openModal === 'unauthorized' && (
            <UnauthorizedModal
              open={openModal === 'unauthorized'}
              onClose={closeModal}
            />
          )}
        </Item>
      </Row>
    </Container>
  );
};

export default ShopContainer;
